import * as React from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';

import { isTestEnvironment } from 'config/msal/configMgmt';
import FeatureFlagManager from 'config/featureFlags/FeatureFlagManager';
import { FeatureFlagName } from 'config/featureFlags';
import { UserContext } from './context/user';
import 'notyf/notyf.min.css';

import LoginPage from './pages/anonymous/Login';
import RegisterByPINPage from './pages/anonymous/RegisterByPIN';
import LegacyLoginPage from './pages/anonymous/Login/legacyLogin';
import Page404 from './pages/errors/404';

import PatientRouting from './pages/patient/Routing';
import ProviderRouting from './pages/provider/Routing';
import FacilityDirectorRouting from './pages/facility_director/Routing';
import FacilityExecutiveRouting from './pages/facility_executive/Routing';
import CorporationRouting from './pages/corporation/Routing';
import SuperCorporationRouting from './pages/super_corporation/Routing';
import AdminRouting from './pages/admin/Routing';
import SuperAdminRouting from './pages/super_admin/Routing';
import { AssessmentPage } from './pages/anonymous/Assessment';
import { StorageConstants } from './constants/storageConstants';
import { AnonymousRoutes } from './constants/routes';
import { AccountDeleteConfirmPage } from './pages/anonymous/AccountDeleteConfirm';
import { PrivacyPolicyPage } from './pages/anonymous/PrivacyPolicy';
import { Roles } from './constants/roles';

/* bundle translations */
import './services/translations/i18n';

const App = (): React.JSX.Element => {
  const { user, Login, patientReflectionsConsentAccepted, patientHipaaConsentAccepted } = React.useContext(UserContext);
  const storage = sessionStorage;
  const isPatientConsentEnabled = FeatureFlagManager.get<boolean>(FeatureFlagName.isPatientConsentEnabled);

  React.useEffect(() => {
    const persistedUserAccount = storage.getItem(StorageConstants.PersistedUserAccount);

    if (persistedUserAccount) {
      const parsed = JSON.parse(persistedUserAccount);
      Login(parsed);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const DetermineRouting = (): React.JSX.Element => {
    const isAuthenticated = useIsAuthenticated();
    let routingElement: React.JSX.Element = (
      <Router>
        <Switch>
          <Route exact path={AnonymousRoutes.LOGIN} component={isTestEnvironment() ? LegacyLoginPage : LoginPage} />
          <Route exact path={AnonymousRoutes.REGISTER_BY_PIN} component={RegisterByPINPage} />
          <Route exact path={AnonymousRoutes.ASSESSMENT} component={AssessmentPage} />
          <Route exact path={AnonymousRoutes.ACCOUNT_DELETE_CONFIRM} component={AccountDeleteConfirmPage} />
          <Route exact path={AnonymousRoutes.PRIVACY_POLICY} component={PrivacyPolicyPage} />
          <Route
            render={(props) => {
              if (!isAuthenticated) {
                // not logged in so redirect to login page with the return url
                // eslint-disable-next-line react/prop-types
                return <Redirect to={{ pathname: AnonymousRoutes.LOGIN, state: { from: props.location } }} />;
              }
              return <Route component={Page404} />;
            }}
          />
        </Switch>
      </Router>
    );

    if (user) {
      switch (user.role) {
        case Roles.PATIENT:
          if (isPatientConsentEnabled) {
            routingElement = <PatientRouting patientConsentAccepted={patientReflectionsConsentAccepted} hipaaConsentAccepted={patientHipaaConsentAccepted} />;
          } else if (!isPatientConsentEnabled) {
            routingElement = <PatientRouting />;
          }
          break;
        case Roles.PROVIDER:
          routingElement = <ProviderRouting />;
          break;
        case Roles.FACILITY_DIRECTOR:
          routingElement = <FacilityDirectorRouting />;
          break;
        case Roles.FACILITY_EXECUTIVE:
          routingElement = <FacilityExecutiveRouting />;
          break;
        case Roles.CORPORATION:
          routingElement = <CorporationRouting />;
          break;
        case Roles.SUPER_CORPORATION:
          routingElement = <SuperCorporationRouting />;
          break;
        case Roles.ADMIN:
          routingElement = <AdminRouting />;
          break;
        case Roles.SUPER_ADMIN:
          routingElement = <SuperAdminRouting />;
          break;
        default:
          break;
      }
    }

    return routingElement;
  };

  return DetermineRouting();
};

export default App;
